import { type ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { type ReactNode, useMemo } from "react"
import { DataGrid } from "@/components/reui/data-grid/data-grid"
import { DataGridTable } from "@/components/reui/data-grid/data-grid-table"
import { cn } from "@/lib/utils"

// Shared hairline table — the columnar "Data table" look copied from the Cash reference
// (CashReviewTable): soft card chrome (rounded + hairline ring + soft elevation), a quiet
// uppercase header row over a hairline, hairline row dividers, a flat hover, and
// right-aligned tabular-nums numeric columns. One definition so every Operations table
// (sales, stock, transfers, reservations) reads identically. Depth is shadow + hairline
// only — never a bevel/emboss edge.
//
// Rendering rides ReUI's data-grid (TanStack Table under Base UI) like the rest of the
// console tables; this wrapper keeps its original `Column<T>` public API so callers and
// tests never see the swap. The caller-supplied `footer` stays a raw <tr> and feeds the
// grid's tfoot slot unchanged.

export type Column<T> = {
  key: string
  header: ReactNode
  align?: "start" | "end"
  headClassName?: string
  cellClassName?: string
  cell: (row: T) => ReactNode
}

export function HairlineTable<T extends object>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  footer,
  minWidthClassName = "min-w-[640px]",
}: {
  columns: Column<T>[]
  rows: T[]
  getRowKey: (row: T, index: number) => string
  onRowClick?: (row: T) => void
  footer?: ReactNode
  minWidthClassName?: string
}) {
  const defs = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((column) => ({
        id: column.key,
        header: () => (
          <span
            className={cn(
              "text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground",
              column.align === "end" && "block text-end",
            )}
          >
            {column.header}
          </span>
        ),
        cell: ({ row }) => column.cell(row.original),
        meta: {
          headerClassName: cn(column.align === "end" ? "text-end" : "text-start", column.headClassName),
          cellClassName: cn(column.align === "end" ? "text-end tabular-nums" : "text-start", column.cellClassName),
        },
      })),
    [columns],
  )

  const table = useReactTable({
    data: rows,
    columns: defs,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => getRowKey(row, index),
  })

  return (
    <div className="overflow-x-auto rounded-lg bg-card ring-1 ring-foreground/5 elevation">
      <DataGrid
        table={table}
        recordCount={rows.length}
        onRowClick={onRowClick}
        tableLayout={{ width: "auto", rowBorder: true }}
        tableClassNames={{
          base: cn("text-[13px]", minWidthClassName),
          footer: "border-t border-border font-medium",
        }}
      >
        <DataGridTable footerContent={footer} />
      </DataGrid>
    </div>
  )
}
